<template>

<div class="flip-state">
    <svg id="svg-flip-state" ref="svg">
      <rect
        v-if="domLoaded"
        x="0"
        y="0"
        :width="width + margin.left + margin.right"
        :height="height + margin.top + margin.bottom"
        fill="gray"
        opacity="0"
        @mouseenter="handleNonStateHover"
        @click="() => handleStateClick('The United States')"
      />
      <g
        v-if="domLoaded"
        :style="{
          transform: `translate(${this.margin.left}px,${this.margin.top}px)`
        }"
      >
        <path
          v-for="state in statesPaths"
          :key="state.properties.name"
          :d="state.path"
          :fill="getStateColor(state.properties)"
          stroke="#FFFFFF"
          stroke-width="1"
          :style="{
            'transform-origin': `${state.centroid[0]}px ${state.centroid[1]}px`
          }"
          :class="`us-state us-state-${state.properties.state_abv}`"
          :state-abv="state.properties.state_abv"
          @mouseenter="() => handleMouseOver(state.properties.name)"
        />
      </g>
    </svg>

  </div>
</template>

<script>
// import { mapGetters, mapActions } from 'vuex'
import {
  getExtentForMetric,
  getColorScaleCurrent,
  getColorScaleElectrified,
  getStateValueMap,
  getColorScaleMap,
  stateABVLookup,
  stateNameLookup
} from '@/utils/dataUtils'
import * as d3 from 'd3'
import * as turf from '@turf/turf'
import _ from 'lodash'
import anime from 'animejs/lib/anime.es.js'
import jsonData from '@/data/current_future_costs.json'
// import textures from "textures";
// const chroma = require("chroma-js");

export default {
  name: 'FlipState',
  props: {
    isElectric: Boolean,
    selectedScenario: String,
    selectedMetric: String,
    selectedFutureMetric: String,
    selectedUSState: String,
    stashedUSState: String,
    selectedExploreView: String,
    selectedFuelType: String
  },
  data: function() {
    return {
      jsonData,
      height: 0,
      width: 0,
      margin: { top: 30, right: 20, bottom: 40, left: 20 },
      marginMobile: { top: 20, right: 0, bottom: 0, left: 0 },
      marginDesktop: { top: 30, right: 20, bottom: 40, left: 20 },
      domLoaded: false,
      stateValueMap: null,
      stateColorScaleMap: null,
      currentColorExtent: {
        max: 0,
        min: 0
      }
    }
  },
  watch: {
    isElectric(newValue) {
      this.handleScenarioChange(newValue)
    },
    selectedScenario(newValue) {
      const newValueMap = getStateValueMap({
        isElectric: this.isElectric,
        selectedScenario: this.selectedScenario,
        selectedMetric: this.selectedMetric,
        selectedFutureMetric: this.selectedFutureMetric
      })
      anime({
        targets: this.$data.stateValueMap,
        ...newValueMap,
        duration: 500,
        easing: 'linear'
      })
    },
    selectedMetric(newValue) {
      this.stateColorScaleMap = this.setStateColorScaleMap(this.isElectric)
      const newValueMap = getStateValueMap({
        isElectric: this.isElectric,
        selectedScenario: this.selectedScenario,
        selectedMetric: this.selectedMetric,
        selectedFutureMetric: this.selectedFutureMetric,
        selectedExploreView: this.selectedExploreView,
        selectedFuelType: this.selectedFuelType
      })
      anime({
        targets: this.$data.stateValueMap,
        ...newValueMap,
        duration: 500,
        easing: 'linear'
      })
    },
    selectedExploreView(newValue) {
      this.stateColorScaleMap = this.setStateColorScaleMap(this.isElectric)
      const newValueMap = getStateValueMap({
        isElectric: this.isElectric,
        selectedScenario: this.selectedScenario,
        selectedMetric: this.selectedMetric,
        selectedFutureMetric: this.selectedFutureMetric,
        selectedExploreView: newValue,
        selectedFuelType: this.selectedFuelType
      })
      anime({
        targets: this.$data.stateValueMap,
        ...newValueMap,
        duration: 500,
        easing: 'linear'
      })
    },
    selectedFuelType(newValue) {
      this.stateColorScaleMap = this.setStateColorScaleMap(this.isElectric)
      const newValueMap = getStateValueMap({
        isElectric: this.isElectric,
        selectedScenario: this.selectedScenario,
        selectedMetric: this.selectedMetric,
        selectedFutureMetric: this.selectedFutureMetric,
        selectedExploreView: this.selectedExploreView,
        selectedFuelType: newValue
      })
      anime({
        targets: this.$data.stateValueMap,
        ...newValueMap,
        duration: 500,
        easing: 'linear'
      })
    }
  },
  methods: {
    // ...mapActions(['setSelectedUSState', 'setStashedUSState']),
    resizeTest() {
      this.onResize()
    },
    onResize() {
      this.width =
        +d3
          .select('#svg-flip-state')
          .style('width')
          .replace('px', '') -
        this.margin.left -
        this.margin.right

      this.height =
        +d3
          .select('#svg-flip-state')
          .style('height')
          .replace('px', '') -
        this.margin.top -
        this.margin.bottom

      if (window.innerWidth < 576) {
        this.margin = this.marginMobile
      } else {
        this.margin = this.marginDesktop
      }
    },
    handleScenarioChange(isElectric) {
      const newValueMap = getStateValueMap({
        isElectric,
        selectedScenario: this.selectedScenario,
        selectedMetric: this.selectedMetric,
        selectedFutureMetric: this.selectedFutureMetric,
        selectedExploreView: this.selectedExploreView,
        selectedFuelType: this.selectedFuelType
      })
      anime({
        targets: '.us-state',
        keyframes: [
          { scale: 0.1, easing: 'easeInSine', duration: 300 },
          {
            fill: el => {
              const stateAbv = el.getAttribute('state-abv')
              const colorScale = this.isElectric
                ? this.colorScaleElectrified
                : this.colorScaleCurrent
              const value = newValueMap[stateAbv]
              return colorScale(value)
            },
            duration: 1
          },
          { scale: 1, easing: 'easeInSine', duration: 200 }
        ],
        delay: anime.stagger(10),
        begin: () => {
          this.highlightLock = true
        },
        complete: () => {
          this.highlightLock = false
          this.stateValueMap = getStateValueMap({
            isElectric: isElectric,
            selectedScenario: this.selectedScenario,
            selectedMetric: this.selectedMetric,
            selectedFutureMetric: this.selectedFutureMetric,
            selectedExploreView: this.selectedExploreView,
            selectedFuelType: this.selectedFuelType
          })
          this.stateColorScaleMap = this.setStateColorScaleMap(isElectric)
        }
      })
    },

    handleMouseOver(value) {
      // this.setSelectedUSState(value)
    },
    handleNonStateHover() {
      // this.setSelectedUSState(this.stashedUSState)
    },
    getStateColor(state) {
      if (this.selectedUSState === 'The United States') {
        return this.stateColorScaleMap[state.state_abv](
          this.stateValueMap[state.state_abv]
        )
      } else {
        if (state.state === this.selectedUSState) {
          return this.stateColorScaleMap[state.state_abv](
            this.stateValueMap[state.state_abv]
          )
        } else {
          return this.stateColorScaleMap[state.state_abv](
            this.stateValueMap[state.state_abv]
          )
        }
      }
    },
    setStateColorScaleMap(isElectric) {
      const retVal = {}
      const keys = Object.keys(this.stateValueMap)
      keys.forEach(key => {
        retVal[key] = isElectric
          ? this.colorScaleElectrified
          : this.colorScaleCurrent
      })
      return retVal
    }
  },
  computed: {
    // ...mapGetters([
    //   'isElectric',
    //   'selectedScenario',
    //   'selectedMetric',
    //   'selectedFutureMetric',
    //   'selectedUSState',
    //   'stashedUSState',
    //   'selectedExploreView',
    //   'selectedFuelType'
    // ]),
    projection() {
      return d3.geoMercator().fitExtent(
        [
          [this.margin.left, this.margin.top],
          [this.width + this.margin.left, this.height + this.margin.top]
        ],
        jsonData
      )
    },
    statesPaths() {
      const pathGenerator = d3.geoPath().projection(this.projection)
      const centerPoint = turf.centroid(jsonData)

      const getCentroid = el => {
        return turf.centroid(el).geometry.coordinates
      }

      const getCenterDist = el => {
        return turf.distance(centerPoint, turf.centroid(el))
      }

      let retVal = jsonData.features.map(el => {
        return {
          properties: el.properties,
          path: pathGenerator(el),
          centroid: this.projection(getCentroid(el)),
          centerDist: getCenterDist(el)
        }
      })

      retVal = _.sortBy(retVal, el => el.centerDist)

      return retVal
    },
    colorScaleCurrent() {
      return getColorScaleCurrent({
        isElectric: false,
        metric: this.selectedMetric,
        selectedExploreView: this.selectedExploreView,
        selectedFuelType: this.selectedFuelType
      })
    },
    colorScaleElectrified() {
      return getColorScaleElectrified({
        isElectric: true,
        metric: this.selectedFutureMetric,
        scenario: this.selectedScenario
      })
    },
    selectedUSStatePath() {
      if (this.selectedUSState === 'The United States') return null

      return this.statesPaths.find(
        el => el.properties.state === this.selectedUSState
      )
    }
  },
  mounted() {
    this.onResize()
    this.domLoaded = true

    this.stateValueMap = getStateValueMap({
      isElectric: this.isElectric,
      selectedScenario: this.selectedScenario,
      selectedMetric: this.selectedMetric,
      selectedFutureMetric: this.selectedFutureMetric,
      selectedExploreView: this.selectedExploreView,
      selectedFuelType: this.selectedFuelType
    })
    this.stateColorScaleMap = this.setStateColorScaleMap(this.isElectric)
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  }
}
</script>

<style lang="scss" scoped>
.flip-state {
  width: 90vw;
  max-width: 90px;

  #svg-flip-state {
      width: 90vw;
      height: 90vh;      
  }
}
</style>
